import { TokenSession } from './auth';
import { public_env } from './constants/public_env';

const NAMED_HTML_ENTITIES: Record<string, string> = {
  nbsp: '\u00a0',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

/**
 * Décode les entités HTML d'un texte issu d'un éditeur rich-text (`&nbsp;`, `&amp;`, `&#39;`,
 * `&#x27;`…) : sans ça, un `&nbsp;` saisi dans l'éditeur ressort tel quel dans les aperçus en
 * texte brut (catalogue, cartes du site). L'espace insécable devient un espace ordinaire.
 */
const MAX_UNICODE_CODE_POINT = 0x10ffff;

/** `String.fromCodePoint` lève une RangeError au-delà de U+10FFFF : on laisse alors l'entité telle quelle. */
const decodeNumericEntity = (match: string, codePoint: number): string =>
  Number.isNaN(codePoint) || codePoint > MAX_UNICODE_CODE_POINT ? match : String.fromCodePoint(codePoint);

export function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&#x([0-9a-f]+);/gi, (match, hex: string): string => decodeNumericEntity(match, Number.parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (match, dec: string): string => decodeNumericEntity(match, Number.parseInt(dec, 10)))
    .replace(/&([a-z]+);/gi, (match, name: string): string => NAMED_HTML_ENTITIES[name.toLowerCase()] ?? match)
    .replace(/\u00a0/g, ' ');
}

/**
 * « Prénom Nom » à partir de champs saisis librement (espaces superflus tolérés, parties vides
 * ignorées), ou `undefined` si aucune des deux n'est renseignée. Source unique pour l'affichage
 * d'une personne (fiche CRM backend, listes admin) : ne pas re-coder ce `join` localement.
 */
export function formatPersonName(firstname?: string | null, lastname?: string | null): string | undefined {
  const name = [firstname, lastname]
    .map((part): string => part?.trim() ?? '')
    .filter((part): boolean => part !== '')
    .join(' ');
  return name === '' ? undefined : name;
}

export function stripHtml(html: string, options: { collapseWhitespace?: boolean } = {}): string {
  const plain = decodeHtmlEntities(html.replace(/<[^>]*>/g, ''));
  return (options.collapseWhitespace ? plain.replace(/\s+/g, ' ') : plain).trim();
}

export const addMinutesToDate = (date: Date, minutes: number) => new Date(date.getTime() + minutes * 60 * 1000);

export function formatDurationFromMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h${String(rest).padStart(2, '0')}`;
}

const pad2 = (value: number) => String(value).padStart(2, '0');

const clampHour = (hour: number): number => Math.max(0, Math.min(23, Math.floor(hour)));

export const formatDateTimeForPicker = (date: Date) => {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
    date.getMinutes(),
  )}:00`;
};

export const parseDateTimeValue = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export function formatDateToDayKey(dateInput: Date | string, timeZone = 'UTC', locale = 'fr-FR'): string | undefined {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) return undefined;

  const parts = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = parts.find((part): boolean => part.type === 'year')?.value;
  const month = parts.find((part): boolean => part.type === 'month')?.value;
  const day = parts.find((part): boolean => part.type === 'day')?.value;

  if (!year || !month || !day) return undefined;

  return `${year}-${month}-${day}`;
}

export function isSameDay(
  firstDateInput: Date | string,
  secondDateInput: Date | string,
  timeZone = 'UTC',
  locale = 'fr-FR',
): boolean {
  return formatDateToDayKey(firstDateInput, timeZone, locale) === formatDateToDayKey(secondDateInput, timeZone, locale);
}

export function formatLocalDateString(dateStr: string, locale: string) {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return undefined;
  const [, year, month, day, hour, minute] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0, 0));
  const formatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  };
  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
}

export const parseHHMMToMinutes = (value: string): number => {
  const [hourString, minuteString] = value.split(':');
  const hour = Number(hourString);
  const minute = Number(minuteString);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0;

  return hour * 60 + minute;
};

export const minutesToHHMM = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${pad2(h)}:${pad2(m)}`;
};

export const MINUTES_IN_DAY = 1440;

// Convention « fin de journée » pour un champ FIN de plage horaire : 1440 minutes (minuit
// exclu du jour courant) s'affiche « 00:00 » — un <input type="time"> ne sait pas rendre
// « 24:00 ». Le >= est défensif : une valeur hors bornes ne produira jamais « 25:00 ».
export const minutesToHHMMEndOfDay = (minutes: number): string =>
  minutes >= MINUTES_IN_DAY ? '00:00' : minutesToHHMM(minutes);

// Réciproque, pour un champ FIN uniquement : « 00:00 » (ou champ vidé → parse 0) = fin de
// journée (1440). Sans ambiguïté : une fin de plage ne peut pas valoir 0 minute.
export const parseHHMMToMinutesEndOfDay = (value: string): number => {
  const minutes = parseHHMMToMinutes(value);
  return minutes <= 0 ? MINUTES_IN_DAY : minutes;
};

// Une plage d'horaires d'ouverture { open, close } « 00:00 → 00:00 » = ouvert 24h/24.
// Source unique de cette convention pour l'éditeur, l'affichage sites custom, le SEO et
// le push Google Business Profile.
export const isAllDayOpeningRange = (range: { open: string; close: string }): boolean =>
  range.open === '00:00' && range.close === '00:00';

export const hourToHHMM = (hour: number): string => `${pad2(clampHour(hour))}:00`;

export const parseHHMMToHour = (value: string): number => {
  const [hourString] = value.split(':');
  const hour = Number(hourString);

  return Number.isFinite(hour) ? clampHour(hour) : 0;
};

export const slugify = (s: string) =>
  (s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

// Pr\u00e9fixe de sous-domaine r\u00e9serv\u00e9 aux previews de site (cf. custom-websites host-routing).
// Un slug de professionnel ne doit JAMAIS commencer par ce pr\u00e9fixe : `preview-<slug>.<domaine>`
// serait alors rout\u00e9 comme une preview et masquerait le vrai site du pro.
export const PREVIEW_SUBDOMAIN_PREFIX = 'preview-';

// Paramètre d'URL d'une preview chargée DANS l'app (iframe de l'écran 8 du tunnel) : la page
// masque alors sa bannière d'inscription et son tracking de funnel, le pro est déjà inscrit.
export const PREVIEW_EMBED_QUERY_PARAM = 'embed';
export const PREVIEW_EMBED_TUNNEL = 'tunnel';

// true si le slug est r\u00e9serv\u00e9 et ne peut pas \u00eatre attribu\u00e9 \u00e0 un professionnel.
export const isReservedProfessionalSlug = (slug: string): boolean =>
  slug.toLowerCase().startsWith(PREVIEW_SUBDOMAIN_PREFIX);

// Rend un slug s\u00fbr pour un professionnel : si le slug candidat tombe sur le pr\u00e9fixe
// r\u00e9serv\u00e9, on le neutralise par un pr\u00e9fixe `pro-` plut\u00f4t que de le router en preview.
export const toSafeProfessionalSlug = (slug: string): string =>
  isReservedProfessionalSlug(slug) ? `pro-${slug}` : slug;

export function formatIsoDateGMTToReadble(
  date_input: string | Date,
  locale: string,
  timezone: string,
  is_short = false,
  with_time = false,
  is_text_short = false,
) {
  const date = typeof date_input === 'string' ? new Date(date_input) : date_input;
  if (!date || isNaN(date.getTime())) return undefined;

  const month: Intl.DateTimeFormatOptions['month'] = is_text_short ? 'short' : is_short ? 'numeric' : 'long';

  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month,
    day: 'numeric',
    timeZone: timezone,
  };
  if (with_time) {
    opts.hour = '2-digit';
    opts.minute = '2-digit';
  }

  return new Intl.DateTimeFormat(locale, opts).format(date);
}

/**
 * Jour mural `YYYY-MM-DD` (sans fuseau, ex. `first_bookable_date`) en libellé court « 18 nov. 2026 » : lu à
 * midi UTC et formaté en UTC, il ne glisse jamais d'un jour, quel que soit le fuseau du pro.
 */
export function formatDayKeyShort(dayKey: string, locale: string): string | undefined {
  return formatIsoDateGMTToReadble(`${dayKey}T12:00:00.000Z`, locale, 'UTC', false, false, true);
}

export function formatIsoTimeGMTToReadble(date_input: string | Date, locale: string, timezone: string) {
  const date = typeof date_input === 'string' ? new Date(date_input) : date_input;
  if (!date || isNaN(date.getTime())) return undefined;

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).format(date);
}

/**
 * Décode un segment base64url (payload de JWT, `state` OAuth signé…) en JSON, UTF-8
 * compris. Lève si le segment est illisible : à l'appelant de choisir son repli.
 */
export function decodeBase64UrlJson(segment: string): unknown {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const jsonPayload = decodeURIComponent(
    atob(padded)
      .split('')
      .map((c: string): string => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join(''),
  );
  return JSON.parse(jsonPayload);
}

export function decodeJWT(token: string): TokenSession {
  return decodeBase64UrlJson(token.split('.')[1]) as TokenSession;
}

/** Balises qui séparent deux textes : sans espace à leur place, « Laye</p><p>Vous » donnerait « LayeVous ». */
const BLOCK_TAG_REGEX = /<\/?(?:p|div|br|li|ul|ol|h[1-6]|tr|td|th|blockquote|section|article|header|footer)\b[^>]*>/gi;

/**
 * Description rich-text réduite en texte brut sur une ligne, pour un aperçu (carte du catalogue,
 * carte du site) : les balises de bloc valent un espace, les autres disparaissent, les entités
 * sont décodées, les blancs repliés.
 */
export function stripHtmlTags(html: string): string {
  return decodeHtmlEntities(html.replace(BLOCK_TAG_REGEX, ' ').replace(/<[^>]*>/g, ''))
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Échappe un texte libre (libellé saisi par un pro, valeur de fiche client)
 * avant de l'insérer dans du HTML : il doit s'afficher tel quel, jamais être
 * interprété comme du balisage.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Entier strictement positif et sûr (`Number.isSafeInteger`) écrit en chiffres
 * décimaux SEULS, sinon `null`. Refuse ce que `Number()` accepterait en silence :
 * `''`, `' 5'`, `'1e3'`, `'0x10'`, `'5.0'`, `'0'`, ou un nombre au-delà de 2^53.
 */
export const parsePositiveSafeInteger = (raw: string): number | null => {
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Id porté par une clé `<prefix><id>` (`custom:12`, `customer:custom:12`,
 * `custom_12`…), ou `null` si la clé ne commence pas par `prefix` ou si la suite
 * n'est pas un id valide (cf. `parsePositiveSafeInteger`).
 *
 * Point de passage UNIQUE des clés « préfixe + id » : cibles d'import, variables
 * de campagne et arbitrages de fusion des champs personnalisés doivent refuser
 * les mêmes clés.
 */
export const parsePrefixedPositiveId = (key: string, prefix: string): number | null =>
  key.startsWith(prefix) ? parsePositiveSafeInteger(key.slice(prefix.length)) : null;

export function isEmpty(data: unknown): data is null | undefined | '' | 'undefined' | 'null' {
  if (data === null || data === undefined) return true;

  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return true;
    return false;
  }

  if (typeof data === 'number' || typeof data === 'boolean') return false;

  if (data instanceof Date) return false;

  if (Array.isArray(data)) {
    return data.length === 0 || data.every(isEmpty);
  }

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 0) return true;
    return keys.every((k): boolean => isEmpty((data as Record<string, unknown>)[k]));
  }

  return false;
}

export function cleanObject(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(cleanObject).filter((item): boolean => !isEmpty(item));
  } else if (obj instanceof Date) {
    return obj;
  } else if (typeof obj === 'object' && obj !== null) {
    const newObj: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleaned = cleanObject(value);
      if (!isEmpty(cleaned)) {
        newObj[key] = cleaned;
      }
    }
    return newObj;
  }

  return obj;
}

// ── Booking cart shared constants ──────────────────────────────────

export const BOOKING_CART_STORAGE_KEY = 'cfixe_booking_cart';
export const BOOKING_STATE_STORAGE_PREFIX = 'cfixe_booking_state_';
export const BOOKING_EVENT_BOOK_PRESTATION = 'cfixe:book-prestation';
export const BOOKING_EVENT_CART_UPDATED = 'cfixe:cart-updated';

// ── Tunnel d'achat carte cadeau / forfait — event d'ouverture en modale in-site ──
/**
 * Émis par les CTA « Offrir » / « Acheter » (prestation-detail, pack-listing) quand le site
 * n'a PAS de page carte cadeau / forfaits dédiée : le site custom l'écoute (GiftTunnelListener)
 * et ouvre le tunnel en modale, sans quitter le site. Detail = {@link GiftTunnelOpenDetail}.
 */
export const GIFT_TUNNEL_EVENT_OPEN = 'cfixe:open-gift-tunnel';

/** Payload de l'event {@link GIFT_TUNNEL_EVENT_OPEN}. */
export interface GiftTunnelOpenDetail {
  scope: 'full' | 'pack';
  preselect?: {
    type?: 'AMOUNT' | 'PACK' | 'PRESTATION';
    packId?: number;
    prestationId?: number;
    /** Durée (min) de la variante choisie sur la page prestation → présélectionne la bonne ligne de prix. */
    durationMinutes?: number;
  };
}

// ── Redirection HTTP → HTTPS ──
const IPV4_HOST_REGEX = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Un host est « public » s'il peut être atteint par un navigateur depuis Internet — donc s'il a un
 * sens de le pousser en HTTPS. Tout le reste est du trafic intra-cluster (probe kubelet sur l'IP du
 * pod, appel de Service par son nom court, curl local) qui n'a ni certificat en face ni duplicate
 * content à corriger, et qu'une redirection ne ferait que casser.
 */
export const isPublicWebHost = (host: string): boolean => {
  // IPv6 littérale : toujours entre crochets dans un en-tête `Host` (`[::1]:3000`).
  if (host.startsWith('[')) return false;
  const hostname = host.split(':')[0]?.toLowerCase() ?? '';
  if (!hostname || hostname === 'localhost') return false;
  if (IPV4_HOST_REGEX.test(hostname)) return false;
  // Un nom sans point ne peut pas être un domaine public : c'est un nom de Service ou de pod k8s.
  return hostname.includes('.');
};

/**
 * URL HTTPS vers laquelle rediriger une requête arrivée en clair, ou `null` s'il ne faut PAS
 * rediriger. Appelé depuis le middleware de chaque app Next (front pro, admin, vitrine, sites
 * clients) — d'où la mise en commun ici plutôt qu'un bloc dupliqué par package.
 *
 * Pourquoi côté app et pas dans Traefik : on ne pose volontairement AUCUNE redirection globale
 * d'entrypoint, car l'entrypoint `web` doit rester en clair pour les challenges HTTP-01 de
 * cert-manager qui activent les domaines clients. Le challenge n'atteint de toute façon jamais
 * une app Next (cert-manager le sert depuis son propre Ingress `cm-acme-http-solver-*`, qui
 * pointe vers un autre Service), donc une redirection applicative ne peut pas le casser.
 *
 * Trois garde-fous :
 * - JAMAIS en local. Sous Tilt, Traefik sert en clair et transmet donc `X-Forwarded-Proto: http`
 *   à juste titre : rediriger enverrait le navigateur sur un `https://localhost:3010` que le
 *   serveur de dev ne sait pas parler (`SSL_ERROR_RX_RECORD_TOO_LONG`). Pire, le 308 est mis en
 *   cache DÉFINITIVEMENT par le navigateur — le dev reste bloqué même une fois le code corrigé.
 * - on n'agit QUE si le proxy annonce explicitement `http`.
 * - et SEULEMENT vers un host public (cf. `isPublicWebHost`). Ce garde-fou n'est pas cosmétique :
 *   il est ce qui protège les probes kubelet. On a d'abord cru qu'un test « == http » suffisait,
 *   puisque la probe tape le pod en direct sans `x-forwarded-proto` — c'est faux. Next POSE
 *   lui-même l'en-tête quand il est absent (`base-server.js` : `req.headers['x-forwarded-proto']
 *   ??= isHttps ? 'https' : 'http'`), donc le middleware voit toujours `http` et redirigeait la
 *   probe vers `https://<ip-du-pod>:3000/…`. La kubelet suit le 308, tombe sur du TLS que le
 *   serveur Next ne parle pas (« server gave HTTP response to HTTPS client »), la liveness tue le
 *   conteneur → CrashLoopBackOff et rolling update bloqué. Le host, lui, distingue vraiment les
 *   deux cas : une probe arrive sur une IP littérale, jamais sur un domaine public.
 * - `host` vide → on s'abstient, sinon on construirait un `https:///…` cassé.
 *
 * Le header peut être une liste (`http, https`) quand plusieurs proxies sont chaînés : le
 * PREMIER élément est le protocole vu par le client, c'est le seul qui nous intéresse.
 *
 * À utiliser avec un 308 (et non un 301) : la méthode et le body sont préservés, donc un POST
 * de formulaire arrivé en HTTP n'est pas dégradé en GET.
 */
export const resolveHttpsRedirectTarget = (
  forwardedProto: string | null | undefined,
  host: string | null | undefined,
  pathWithSearch: string,
  // Paramètre plutôt que lecture directe de `public_env` : la CI substitue le fichier au build
  // (`public_env.prod.ts` → `public_env.ts`), donc les tests tournent toujours en 'local' et ne
  // pourraient rien couvrir d'autre. Les appelants n'y touchent pas.
  environment: string = public_env.ENVIRONMENT,
): string | null => {
  if (environment === 'local') return null;
  if (!host) return null;
  if (!isPublicWebHost(host)) return null;
  if (forwardedProto?.split(',')[0]?.trim() !== 'http') return null;
  return `https://${host}${pathWithSearch}`;
};
